import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import FilterChip from 'LuxKit/Chips/FilterChip'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import OfferSortControl from 'components/Common/OfferSortControl/OfferSortControl'
import Group from 'components/utils/Group'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import type { OfferListSortOption } from 'constants/offerListFilters'
import { getAppliedFilterCount } from 'components/Search/utils'
import { getHotelEnabledFilters } from 'selectors/filters/filterSelectors'
import { useAppSelector } from 'hooks/reduxHooks'

/** At/below this width the map chip uses the short “Map” label so it stays on-screen. */
const MAP_LABEL_NARROW_MAX_PX = 350

const Container = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  padding-top: ${rem(6)};
  padding-bottom: ${rem(16)};
  /* Avoid theme.shadow.bottom.small — its blur bleeds upward. */
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.03);
`

/** Scrolls with the chips so edge padding isn’t clipped off on tiny viewports. */
const ScrollRow = styled.div`
  overflow-x: auto;
  overscroll-behavior-x: contain;
  padding-inline: ${rem(16)};
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const Chips = styled(Group)`
  /* Fill the row so space-between still spreads chips when they fit. */
  min-width: 100%;
  width: max-content;

  > * {
    flex-shrink: 0;
  }
`

const WideMapLabel = styled.span`
  @media (max-width: ${MAP_LABEL_NARROW_MAX_PX}px) {
    display: none;
  }
`

const NarrowMapLabel = styled.span`
  display: none;

  @media (max-width: ${MAP_LABEL_NARROW_MAX_PX}px) {
    display: inline;
  }
`

type Props = {
  filters: App.OfferListFilters
  sortOptions: Array<OfferListSortOption>
  mapViewUrl: string
  onSortChange: ComponentProps<typeof OfferSortControl>['onSortChange']
}

function MetaSearchV2SortRow({
  filters,
  sortOptions,
  mapViewUrl,
  onSortChange,
}: Props) {
  const intl = useIntl()
  const { toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const enabledFilters = useAppSelector(getHotelEnabledFilters)
  const filterCount = getAppliedFilterCount(filters, enabledFilters, true)

  const allFiltersText = intl.formatMessage({
    id: '1CpQrQpseRhWVRxZvWhW',
    defaultMessage: 'All filters',
    description: 'Form field label for button that opens all filters panel',
  })

  const filterCountLabel = filterCount ? intl.formatMessage(
    {
      id: 'kP4ohcXYgX3_NhsaOJVg',
      defaultMessage: '({filterCount, number})',
      description: 'Active filter count in parentheses in the search or listing filters panel',
    },
    { filterCount },
  ) : ''

  const seeMapText = intl.formatMessage({
    id: 'aWNRw8HDoLcWVDTYQjnX',
    defaultMessage: 'See on map',
    description: 'Filter pill label that navigates from meta search results to the map view',
  })

  const mapText = intl.formatMessage({
    id: '3vQcR7Ak_LZ7jtoA_6hd',
    defaultMessage: 'Map',
    description: 'Short filter pill label for map view on very narrow meta search viewports',
  })

  return (
    <Container>
      <ScrollRow>
        <Chips direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8} wrap="nowrap">
          <FilterChip
            size="small"
            startIcon={<LineFilterAltIcon />}
            onClick={toggleFilterPanel}
            selected={filterCount > 0}
          >
            {allFiltersText} {filterCountLabel}
          </FilterChip>
          {!!sortOptions.length && (
            <OfferSortControl
              sortOptions={sortOptions}
              roundedButton
              showSelectedHighlight
              filterChipSize="small"
              onSortChange={onSortChange}
            />
          )}
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            to={mapViewUrl}
          >
            <WideMapLabel>{seeMapText}</WideMapLabel>
            <NarrowMapLabel>{mapText}</NarrowMapLabel>
          </FilterChip>
        </Chips>
      </ScrollRow>
    </Container>
  )
}

export default MetaSearchV2SortRow
